import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Reports Service - API calls for financial reports
 */
export const reportsService = {
    /**
     * Get Profit & Loss statement
     */
    async getProfitLossReport(filters) {
        try {
            const params = new URLSearchParams(filters);

            const response = await fetch(
                `${API_BASE_URL}/reports/profit-loss?${params}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch P&L report');
            }

            const data = await response.json();

            if (!data.success) return null;

            // Transform backend response to match frontend expected structure
            const backendData = data.data;

            return {
                summary: {
                    total_revenue: backendData.revenue?.net_revenue || 0,
                    total_expenses: backendData.expenses?.total_expenses || 0,
                    gross_profit: backendData.profitability?.gross_profit || 0,
                    net_profit: backendData.profitability?.gross_profit || 0, // Using gross_profit as net_profit if not available
                    gross_margin_percent: backendData.profitability?.gross_margin_percent || 0,
                    net_margin_percent: backendData.profitability?.gross_margin_percent || 0,
                    operating_expenses: 0
                },
                trend: backendData.trend || [],
                period: backendData.period,
                date_range: backendData.date_range
            };
        } catch (error) {
            console.error('Error fetching P&L report:', error);
            throw error;
        }
    },

    /**
     * Get product profitability report
     */
    async getProductProfitabilityReport(startDate, endDate) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/product-profitability?start_date=${startDate}&end_date=${endDate}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch product profitability report');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching product profitability report:', error);
            throw error;
        }
    },

    /**
     * Get customer profitability report
     */
    async getCustomerProfitabilityReport(startDate, endDate) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/customer-profitability?start_date=${startDate}&end_date=${endDate}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch customer profitability report');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching customer profitability report:', error);
            throw error;
        }
    },

    /**
     * Get payment method analysis
     */
    async getPaymentMethodAnalysis(startDate, endDate) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/payment-method-analysis?start_date=${startDate}&end_date=${endDate}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch payment method analysis');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching payment method analysis:', error);
            throw error;
        }
    },

    /**
     * Get expense breakdown report
     */
    async getExpenseBreakdownReport(startDate, endDate, groupBy = 'category') {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/expense-breakdown?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch expense breakdown report');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching expense breakdown report:', error);
            throw error;
        }
    },

    /**
     * Get revenue breakdown report
     */
    async getRevenueBreakdownReport(startDate, endDate) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/revenue-breakdown?start_date=${startDate}&end_date=${endDate}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch revenue breakdown report');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching revenue breakdown report:', error);
            throw error;
        }
    },

    /**
     * Get monthly summary report
     */
    async getMonthlySummaryReport(accountingPeriod) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/monthly-summary?accounting_period=${accountingPeriod}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch monthly summary report');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching monthly summary report:', error);
            throw error;
        }
    },

    /**
     * Get dashboard metrics
     */
    async getDashboardMetrics(period = 'current_month') {
        try {
            const response = await fetch(
                `${API_BASE_URL}/reports/dashboard-metrics?period=${period}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch dashboard metrics');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching dashboard metrics:', error);
            throw error;
        }
    }
};
